import { jsx as _jsx } from "react/jsx-runtime";
import { SpecraLogo, SpecraProgramSidebar, SpecraWordmark } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { BadgeCheck, Boxes, CalendarDays, Crosshair, FileBadge, FileText, FolderKanban, FolderOpen, LayoutDashboard, Library, Link2, MessageSquare, Network, Shield, Target, Wrench } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/authContext';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { renderRouterLink } from '../lib/renderRouterLink';
import { useLayoutStore } from '../lib/stores/LayoutStore';
import { SidebarUserMenu } from './SidebarUserMenu';
const isDev = process.env.NODE_ENV === 'development';
// Roles that see the Admin console entry. Mirrors RequireAdminRoute /
// enies-lobby AdminRoles (case-normalized) — unchanged from GlobalNavItems.
const ADMIN_ROLES = new Set([
    'admin',
    'internal'
]);
const iconClass = 'w-4 h-4 flex-shrink-0';
// The delivery section pages (mirrors the /delivery hub grid, minus Registries
// which is a tenant-wide root asset with its own top-level entry). These fold
// in beneath the Delivery item while the user is anywhere in /delivery/*, so the
// section is navigable without bouncing off the landing grid. Registries is
// intentionally excluded — it lives at the root and leaving to it would collapse
// this sub-nav.
const DELIVERY_SECTIONS = [
    {
        id: 'delivery-command-center',
        label: 'Command Center',
        href: '/delivery/command-center',
        icon: /*#__PURE__*/ _jsx(LayoutDashboard, {
            className: iconClass
        }),
        indented: true,
        testId: Testids.delivery.subnavCommandCenter
    },
    {
        id: 'delivery-org',
        label: 'Organization',
        href: '/delivery/org',
        icon: /*#__PURE__*/ _jsx(Network, {
            className: iconClass
        }),
        indented: true,
        testId: Testids.delivery.subnavOrg
    },
    {
        id: 'delivery-programs',
        label: 'Programs',
        href: '/delivery/programs',
        icon: /*#__PURE__*/ _jsx(FolderKanban, {
            className: iconClass
        }),
        indented: true,
        testId: Testids.delivery.subnavPrograms
    },
    {
        id: 'delivery-audit-templates',
        label: 'Audit Templates',
        href: '/delivery/audit-templates',
        icon: /*#__PURE__*/ _jsx(FileBadge, {
            className: iconClass
        }),
        indented: true,
        testId: Testids.delivery.subnavAuditTemplates
    },
    {
        id: 'delivery-evidence-board',
        label: 'Proof & Evidence Board',
        href: '/delivery/evidence-board',
        icon: /*#__PURE__*/ _jsx(BadgeCheck, {
            className: iconClass
        }),
        indented: true,
        testId: Testids.delivery.subnavEvidenceBoard
    },
    {
        id: 'delivery-calendar',
        label: 'Delivery Calendar',
        href: '/delivery/calendar',
        icon: /*#__PURE__*/ _jsx(CalendarDays, {
            className: iconClass
        }),
        indented: true,
        testId: Testids.delivery.subnavCalendar
    }
];
/**
 * Application-level left navigation (outside a program). Mirrors the redesign
 * mockup's app taxonomy — Dashboard, then Workflows / Tools / Admin groups —
 * reconciled to the routes that actually exist (mockup stubs like Organization
 * / Integrations are omitted; Opportunities and SharePoint are real routes the
 * mockup lacks, placed in the nearest group). Renders through the shared
 * SpecraProgramSidebar shell with a logo brand instead of a program identity.
 */ export function AppSidebar() {
    const location = useLocation();
    const { user } = useAuth();
    // Collapse lives in the layout store so a focus surface (the in-flow
    // document editor) can fold the rail and hand its width to the editor.
    const desktopCollapsed = useLayoutStore((state)=>state.sidebarCollapsed);
    const isNarrow = useMediaQuery('(max-width: 767px)');
    const collapsed = isNarrow || desktopCollapsed;
    const toggleCollapsed = useLayoutStore((state)=>state.toggleSidebarCollapsed);
    const [groupState, setGroupState] = useState({});
    const isAdmin = !!user?.role && ADMIN_ROLES.has(user.role.toLowerCase());
    // Fold the delivery section pages in beneath Delivery whenever we're anywhere
    // in /delivery/* (the app sidebar itself only renders on the section pages —
    // opening a specific program swaps the whole rail to ProgramSidebar).
    const inDelivery = location.pathname === '/delivery' || location.pathname.startsWith('/delivery/');
    // ⌘\ / Ctrl+\ toggles the rail (LAYOUT-SPEC §7), matching the program rail.
    useEffect(()=>{
        const onKeyDown = (event)=>{
            if (event.key === '\\' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                toggleCollapsed();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return ()=>window.removeEventListener('keydown', onKeyDown);
    }, [
        toggleCollapsed
    ]);
    const primary = useMemo(()=>[
            {
                id: 'dashboard',
                label: 'Dashboard',
                href: '/dashboard',
                icon: /*#__PURE__*/ _jsx(LayoutDashboard, {
                    className: iconClass
                })
            }
        ], []);
    const groupDefs = useMemo(()=>[
            {
                id: 'workflows',
                label: 'Workflows',
                items: [
                    {
                        id: 'capture',
                        label: 'Pursuits',
                        href: '/capture',
                        icon: /*#__PURE__*/ _jsx(Crosshair, {
                            className: iconClass
                        })
                    },
                    {
                        id: 'delivery',
                        label: 'Delivery',
                        href: '/delivery',
                        icon: /*#__PURE__*/ _jsx(Boxes, {
                            className: iconClass
                        }),
                        testId: Testids.delivery.navItem
                    },
                    ...inDelivery && !collapsed ? DELIVERY_SECTIONS : [],
                    {
                        id: 'projects',
                        label: 'Projects',
                        href: '/projects',
                        icon: /*#__PURE__*/ _jsx(FolderOpen, {
                            className: iconClass
                        })
                    },
                    {
                        id: 'opportunities',
                        label: 'Opportunities',
                        href: '/opportunities',
                        icon: /*#__PURE__*/ _jsx(Target, {
                            className: iconClass
                        })
                    }
                ]
            },
            // Registries sit at the root, not under Delivery — they're tenant-wide
            // assets (companies, contacts, capabilities, vehicles, role types)
            // consumed by capture and proposal too. Label-less group renders as a
            // standalone entry; it grows into a labeled group as more registry
            // pages (labor categories) land.
            {
                id: 'registries',
                items: [
                    {
                        id: 'registries',
                        label: 'Registries',
                        href: '/registries',
                        icon: /*#__PURE__*/ _jsx(Library, {
                            className: iconClass
                        }),
                        testId: Testids.registries.navItem
                    }
                ]
            },
            {
                id: 'tools',
                label: 'Tools',
                items: [
                    {
                        id: 'chat',
                        label: 'Chat',
                        // Resume the last chat; stay active across the whole section.
                        href: '/assistant',
                        matchPath: '/assistant',
                        icon: /*#__PURE__*/ _jsx(MessageSquare, {
                            className: iconClass
                        })
                    },
                    {
                        id: 'documents',
                        label: 'Documents',
                        href: '/documents',
                        icon: /*#__PURE__*/ _jsx(FileText, {
                            className: iconClass
                        })
                    },
                    {
                        id: 'sharepoint',
                        label: 'SharePoint',
                        href: '/sharepoint-documents',
                        icon: /*#__PURE__*/ _jsx(Link2, {
                            className: iconClass
                        })
                    },
                    // D12: every member can delegate their own access, not just
                    // grantors/admins — AssignmentsListPage itself picks the
                    // self-service vs. power-tool view server-side, so this entry is
                    // never gated on ADMIN_ROLES.
                    {
                        id: 'delegate-access',
                        label: 'Delegate my access',
                        href: '/admin/access',
                        icon: /*#__PURE__*/ _jsx(Shield, {
                            className: iconClass
                        }),
                        testId: Testids.adminAccess.navItem
                    }
                ]
            }
        ], [
        inDelivery,
        collapsed
    ]);
    const isActive = useCallback((def)=>{
        const path = def.matchPath ?? def.href;
        return location.pathname === path || location.pathname.startsWith(`${path}/`);
    }, [
        location.pathname
    ]);
    const toItem = useCallback((def)=>({
            id: def.id,
            label: def.label,
            icon: def.icon,
            href: def.href,
            active: isActive(def),
            testId: def.testId,
            indented: def.indented
        }), [
        isActive
    ]);
    const groups = useMemo(()=>{
        const adminGroup = isAdmin ? [
            {
                id: 'admin',
                label: 'Admin',
                href: '/admin',
                icon: /*#__PURE__*/ _jsx(Shield, {
                    className: iconClass
                }),
                testId: Testids.adminConsole.nav
            }
        ] : [];
        const devGroup = isDev ? [
            {
                id: 'dev-tools',
                label: 'Tools Library (Dev)',
                href: '/dev/tools',
                icon: /*#__PURE__*/ _jsx(Wrench, {
                    className: iconClass
                })
            }
        ] : [];
        return [
            {
                id: 'primary',
                items: primary.map(toItem)
            },
            ...groupDefs.map((group)=>({
                    id: group.id,
                    label: group.label,
                    items: group.items.map(toItem)
                })),
            ...adminGroup.length ? [
                {
                    id: 'admin',
                    label: 'Admin',
                    items: adminGroup.map(toItem)
                }
            ] : [],
            ...devGroup.length ? [
                {
                    id: 'dev',
                    items: devGroup.map(toItem)
                }
            ] : []
        ];
    }, [
        groupDefs,
        isAdmin,
        primary,
        toItem
    ]);
    return /*#__PURE__*/ _jsx(SpecraProgramSidebar, {
        brand: /*#__PURE__*/ _jsx(Link, {
            className: "hover:opacity-80 transition-opacity",
            title: "Home",
            to: "/dashboard",
            children: collapsed ? /*#__PURE__*/ _jsx(SpecraLogo, {
                variant: "mark",
                size: 22
            }) : /*#__PURE__*/ _jsx(SpecraWordmark, {
                height: 16
            })
        }),
        className: "h-full max-md:max-h-none max-md:w-[var(--component-sidebar-collapsed-width)]",
        collapsed: collapsed,
        collapseToggleTestId: Testids.appNav.collapse,
        "data-testid": Testids.appNav.sidebar,
        footer: user ? /*#__PURE__*/ _jsx(SidebarUserMenu, {
            collapsed: collapsed,
            user: user
        }) : undefined,
        getGroupProps: (group)=>group.label ? {
                collapsible: true,
                containsActive: group.items.some((item)=>item.active),
                labelTestId: Testids.appNav.groupLabel,
                onToggle: ()=>setGroupState((state)=>({
                            ...state,
                            [group.id]: !(state[group.id] ?? true)
                        })),
                open: groupState[group.id] ?? true
            } : {},
        groups: groups,
        navigationLabel: "Main navigation",
        onCollapseToggle: isNarrow ? undefined : toggleCollapsed,
        renderLink: renderRouterLink
    });
}
